import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, X, Ruler, Scale, Thermometer, Square, Box, Clock, Gauge, HardDrive, Activity, Zap, Move, RotateCw, Radio, TrendingUp, Layers, Settings, RefreshCw, Circle, Fuel, Flame, Droplets, Droplet, Sun, Lightbulb, Sparkles, Image, Waves, Battery, CircuitBoard, Cpu, Codesandbox, Plug, Magnet, Disc, Compass, Radiation, Atom, Hash, Wifi, Volume2, Type, TreeDeciduous } from 'lucide-react';
import { converterCategories } from '../data/mock';
import LocalizedLink from './LocalizedLink';

// Map converter IDs to their icons
const converterIconMap = {
  'length': Ruler,
  'weight': Scale,
  'volume': Box,
  'temperature': Thermometer,
  'area': Square,
  'pressure': Gauge,
  'energy': Zap,
  'volume-dry': Box,
  'currency': TrendingUp,
  'case': Type,
  'power': Activity,
  'force': Move,
  'time': Clock,
  'speed': Gauge,
  'angle': RotateCw,
  'fuel': Fuel,
  'numbers': Hash,
  'data-storage': HardDrive,
  'velocity-angular': RotateCw,
  'acceleration': TrendingUp,
  'acceleration-angular': RefreshCw,
  'density': Layers,
  'specific-volume': Box,
  'moment-inertia': Circle,
  'moment-force': Move,
  'torque': Settings,
  'fuel-efficiency-mass': Fuel,
  'fuel-efficiency-volume': Fuel,
  'temperature-interval': Thermometer,
  'thermal-expansion': Thermometer,
  'thermal-resistance': Thermometer,
  'thermal-conductivity': Thermometer,
  'specific-heat-capacity': Thermometer,
  'heat-density': Flame,
  'heat-flux-density': Flame,
  'heat-transfer-coefficient': Flame,
  'flow': Droplets,
  'flow-mass': Droplets,
  'flow-molar': Droplets,
  'mass-flux-density': Droplets,
  'concentration-molar': Droplet,
  'concentration-solution': Droplet,
  'viscosity-dynamic': Droplet,
  'viscosity-kinematic': Droplet,
  'surface-tension': Droplet,
  'permeability': Droplet,
  'luminance': Sun,
  'luminous-intensity': Sparkles,
  'illumination': Lightbulb,
  'digital-image-resolution': Image,
  'frequency-wavelength': Waves,
  'charge': Battery,
  'linear-charge-density': Battery,
  'surface-charge-density': Battery,
  'volume-charge-density': Battery,
  'current': Zap,
  'linear-current-density': Zap,
  'surface-current-density': Zap,
  'electric-field-strength': Zap,
  'electric-potential': Plug,
  'electric-resistance': CircuitBoard,
  'electric-resistivity': CircuitBoard,
  'electric-conductance': CircuitBoard,
  'electric-conductivity': CircuitBoard,
  'electrostatic-capacitance': Cpu,
  'inductance': Codesandbox,
  'magnetomotive-force': Magnet,
  'magnetic-field-strength': Compass,
  'magnetic-flux': Magnet,
  'magnetic-flux-density': Disc,
  'radiation': Radiation,
  'radiation-activity': Atom,
  'radiation-exposure': Radiation,
  'radiation-absorbed-dose': Radiation,
  'prefixes': Hash,
  'data-transfer': Wifi,
  'sound': Volume2,
  'typography': Type,
  'volume-lumber': TreeDeciduous
};

const getIconForConverter = (converterId) => {
  return converterIconMap[converterId] || null;
};

const Sidebar = ({ isOpen, onClose, onSelectConverter }) => {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState(['common']);

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev =>
      prev.includes(categoryKey)
        ? prev.filter(k => k !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const categoryKeys = Object.keys(converterCategories);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-50 lg:z-auto
        w-72 h-screen lg:h-auto
        bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        lg:transform-none overflow-y-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Mobile close button */}
        <div className="lg:hidden flex justify-between items-center p-4 border-b">
          <span className="font-semibold text-gray-700">{t('common.allConverters')}</span>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        <nav className="p-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3 hidden lg:block">
            {t('common.allConverters')}
          </h3>

          {categoryKeys.map((key) => {
            const category = converterCategories[key];
            const isExpanded = expandedCategories.includes(key);

            return (
              <div key={key} className="mb-1">
                <button
                  onClick={() => toggleCategory(key)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                >
                  <span className="font-medium">{t(`sidebar.categories.${key}`, category.title)}</span>
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400" />
                  )}
                </button>

                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {category.items.map((item) => {
                      const IconComponent = getIconForConverter(item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            onSelectConverter(key, item.id);
                            onClose();
                          }}
                          className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
                        >
                          {IconComponent && <IconComponent size={14} />}
                          {t(`sidebar.converters.${item.id}`, item.name)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Quick Links to Individual Converters - Top 10 */}
          <div className="mt-3 pt-3 border-t">
            <h4 className="text-xs font-semibold text-gray-400 uppercase px-3 mb-2">
              {t('sidebar.quickLinks')}
            </h4>
            <LocalizedLink
              to="/convert/length"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Ruler size={14} />
              {t('converters.length.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/weight"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Scale size={14} />
              {t('converters.weight.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/temperature"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Thermometer size={14} />
              {t('converters.temperature.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/area"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Square size={14} />
              {t('converters.area.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/volume"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Box size={14} />
              {t('converters.volume.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/time"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Clock size={14} />
              {t('converters.time.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/speed"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Gauge size={14} />
              {t('converters.speed.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/data-storage"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <HardDrive size={14} />
              {t('converters.digital.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/pressure"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Gauge size={14} />
              {t('converters.pressure.name')}
            </LocalizedLink>
            <LocalizedLink
              to="/convert/energy"
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              <Zap size={14} />
              {t('converters.energy.name')}
            </LocalizedLink>
          </div>

          <div className="mt-3 pt-3 border-t">
            <LocalizedLink
              to="/page/common-unit-systems"
              onClick={onClose}
              className="block px-3 py-2 text-sm text-[#2563eb] hover:text-[#1d4ed8] hover:bg-blue-50 rounded transition-colors"
            >
              {t('sidebar.commonUnitSystems')}
            </LocalizedLink>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
